import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, Building2, BookOpen, Users,
  ClipboardList, BarChart2, LogOut, Layers, Calendar, FilePlus, UploadCloud, ListChecks, Zap, FileText
} from 'lucide-react';

const ADMIN_BASE_ITEMS = [
  { to: '/coaching-admin',          label: 'Dashboard',      icon: LayoutDashboard },
  { to: '/coaching-admin/batches',  label: 'Batches',        icon: Layers          },
  { to: '/coaching-admin/students', label: 'Students',       icon: Users           },
  { to: '/coaching-admin/tests',    label: 'Test Assignment', icon: ClipboardList  },
  { to: '/coaching-admin/schedule', label: 'Schedule',       icon: Calendar        },
  { to: '/coaching-admin/results',  label: 'Results',        icon: BarChart2       },
];

const NAV_ITEMS = {
  super_admin: [
    { to: '/super-admin/dashboard',  label: 'Dashboard',    icon: LayoutDashboard },
    { to: '/super-admin/coachings',  label: 'Coachings',    icon: Building2       },
    { to: '/super-admin/upload-test',label: 'Upload Test',  icon: UploadCloud     },
    { to: '/super-admin/my-tests',   label: 'My Tests',     icon: ListChecks      },
    { to: '/super-admin/create-test',label: 'Create Test',  icon: FilePlus        },
    { to: '/super-admin/questions',  label: 'Question Bank',icon: BookOpen        },
    { to: '/super-admin/analytics',  label: 'Analytics',    icon: BarChart2       },
    { to: '/super-admin/attempts-report', label: 'Attempts Report', icon: FileText },
  ],
  teacher: [
    { to: '/teacher', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/teacher/generate', label: 'Generate Test', icon: Zap },
    { to: '/teacher/tests', label: 'My Tests', icon: FileText },
  ],
  student: [
    { to: '/student', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/student/tests', label: 'My Tests', icon: ClipboardList },
    { to: '/student/results', label: 'Results', icon: BarChart2 },
  ],
};

const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/uploads';

export default function Sidebar({ isOpen, onClose }) {
  const branding = useBranding();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const items = user?.role === 'coaching_admin'
    ? [
        ...ADMIN_BASE_ITEMS,
        ...(branding.planType === 'plan_b' || branding.planType === 'both'
          ? [{ to: '/coaching-admin/generate', label: 'Generate Test', icon: Zap }]
          : []),
      ]
    : NAV_ITEMS[user?.role] || [];

  const handleLogout = () => {
    const role = user?.role;
    logout();
    navigate(role === 'super_admin' ? '/super-admin' : '/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-64 z-50 flex flex-col transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{ backgroundColor: branding.primaryColor || '#1A1A2E' }}
      >
        {/* Logo area */}
        <div className="h-16 flex items-center gap-2 px-4 border-b border-white/10">
          {branding.logoSmall && (
            <img
              src={`${STORAGE_URL}${branding.logoSmall}`}
              alt={branding.name}
              className="h-8 w-auto object-contain flex-shrink-0"
            />
          )}
          <span className="text-white font-bold text-lg truncate">{branding.name}</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to.split('/').length === 2}
              onClick={onClose}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="sidebar-item w-full">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
